import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../shared/SimulatorShell.css';
import './stylings/CFGSimulator.css';
import Transport from '../shared/Transport';
import { CFGTestCases } from './CFGTestCases';
import { ParseTree } from './ParseTree';
import { VariablesEditor } from './VariablesEditor';
import { TerminalsEditor } from './TerminalsEditor';
import { ProductionRulesEditor } from './ProductionRulesEditor';
import { CollapsibleSection } from '../shared/CollapsibleSection';
import { useExamples } from './examples';
import { useCFG } from './useCFG';
import { validateCFGChallenge } from '../Tutorial_components/ChallengeValidator';
import { CheckCircle, Target } from 'lucide-react';

const CFGSimulator = ({ challenge, tutorialDemoKey, onTutorialDemoConsumed }) => {
    const { examples } = useExamples();
    const [currentExampleName, setCurrentExampleName] = useState(challenge ? null : 'balanced_parentheses');
    const [currentExampleDescription, setCurrentExampleDescription] = useState(null);
    const [validationResults, setValidationResults] = useState(null);

    // Memoize initialConfig to prevent re-renders
    const initialConfig = useMemo(() => challenge ? {
        variables: challenge.challenge?.variables || ['S'],
        terminals: challenge.challenge?.terminals || ['0', '1'],
        rules: [],
        startVariable: challenge.challenge?.variables?.[0] || 'S',
    } : {
        variables: examples['balanced_parentheses'].variables,
        terminals: examples['balanced_parentheses'].terminals,
        rules: examples['balanced_parentheses'].rules,
        startVariable: examples['balanced_parentheses'].startVariable,
    }, [challenge, examples]);

    const cfg = useCFG(initialConfig);

    const [inputString, setInputString] = useState('');
    const [derivationSteps, setDerivationSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState(-1);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1000);
    const [isAccepted, setIsAccepted] = useState(null);

    const resetDerivation = useCallback(() => {
        setDerivationSteps([]);
        setCurrentStep(-1);
        setIsPlaying(false);
        setIsAccepted(null);
    }, []);

    // Event listeners for toolbox actions
    useEffect(() => {
        const handleImport = () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        try {
                            const cfgDefinition = JSON.parse(e.target.result);
                            cfg.loadCFG({
                                variables: cfgDefinition.variables || [],
                                terminals: cfgDefinition.terminals || [],
                                rules: cfgDefinition.rules || [],
                                startVariable: cfgDefinition.startVariable || 'S'
                            });
                            setCurrentExampleName(cfgDefinition.name || 'Imported CFG');
                            resetDerivation();
                        } catch (error) {
                            alert('Invalid JSON file or CFG definition format');
                        }
                    };
                    reader.readAsText(file);
                }
            };
            input.click();
        };

        const handleExport = () => {
            const cfgDefinition = {
                name: currentExampleName || 'Custom CFG',
                description: 'Exported CFG definition',
                variables: cfg.variables,
                terminals: cfg.terminals,
                rules: cfg.rules,
                startVariable: cfg.startVariable
            };
            const dataStr = JSON.stringify(cfgDefinition, null, 2);
            const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', dataUri);
            linkElement.setAttribute('download', 'cfg_definition.json');
            linkElement.click();
        };

        const handleClearAll = () => {
            if (window.confirm('Are you sure you want to clear all and start fresh?')) {
                cfg.loadCFG({ variables: ['S'], terminals: ['a', 'b'], rules: [], startVariable: 'S' });
                setCurrentExampleName(null);
                setCurrentExampleDescription(null);
                resetDerivation();
                setValidationResults(null);
            }
        };

        window.addEventListener('import', handleImport);
        window.addEventListener('export', handleExport);
        window.addEventListener('clearAll', handleClearAll);
        return () => {
            window.removeEventListener('import', handleImport);
            window.removeEventListener('export', handleExport);
            window.removeEventListener('clearAll', handleClearAll);
        };
        // Use individual cfg properties to avoid re-running when cfg object changes
    }, [cfg.loadCFG, cfg.variables, cfg.terminals, cfg.rules, cfg.startVariable, currentExampleName, resetDerivation]);

    // Reset to blank when challenge mode is activated
    useEffect(() => {
        if (challenge) {
            cfg.loadCFG({
                variables: challenge.challenge?.variables || ['S'],
                terminals: challenge.challenge?.terminals || ['0', '1'],
                rules: [],
                startVariable: challenge.challenge?.variables?.[0] || 'S'
            });
            setInputString('');
            resetDerivation();
            setValidationResults(null);
        }
        // Only run when challenge changes
    }, [challenge]);

    // Auto-play derivation steps
    useEffect(() => {
        let timer;
        if (isPlaying && currentStep < derivationSteps.length - 1) {
            timer = setTimeout(() => { setCurrentStep(currentStep + 1); }, playbackSpeed);
        } else if (currentStep >= derivationSteps.length - 1) {
            setIsPlaying(false);
        }
        return () => clearTimeout(timer);
    }, [isPlaying, currentStep, derivationSteps.length, playbackSpeed]);

    // Dual-mode: token mode activates when any terminal is multi-character (e.g. "if", "then")
    const detectTokenMode = (cfg) => cfg.terminals.some(t => t.length > 1);

    const tokenizeRHS = (rhs, tokenMode) => {
        if (rhs === 'ε' || rhs === 'epsilon' || rhs === '') return [];
        return tokenMode ? rhs.trim().split(/\s+/) : rhs.split('');
    };

    const tokenizeInput = (input, tokenMode) => {
        if (!input || input === '') return [];
        return tokenMode ? input.trim().split(/\s+/) : input.split('');
    };

    // Helper functions for CNF conversion and CYK parsing
    const normalizeCFG = (cfg, tokenMode) => {
        const newCfg = JSON.parse(JSON.stringify(cfg));
        if (!Array.isArray(newCfg.rules)) newCfg.rules = [];
        newCfg.rules = newCfg.rules.map(r => ({
            left: r.left,
            right: Array.isArray(r.right) ? r.right
                : ((r.right === 'ε' || r.right === 'epsilon' || r.right === '') ? [] : tokenizeRHS(r.right, tokenMode))
        }));
        return newCfg;
    };

    const toCNF = (cfg, tokenMode) => {
        let cnf = normalizeCFG(cfg, tokenMode);
        cnf = eliminateStart(cnf);
        cnf = eliminateNull(cnf);
        cnf = eliminateUnit(cnf);
        cnf = separateTerminals(cnf);
        cnf = binarize(cnf);
        cnf = removeDuplicates(cnf);
        cnf.rules.sort((a, b) => {
            const leftComparison = a.left.localeCompare(b.left);
            if (leftComparison !== 0) return leftComparison;
            return a.right.join(' ').localeCompare(b.right.join(' '));
        });
        return cnf;
    };

    const eliminateStart = (cfg) => {
        const newCfg = JSON.parse(JSON.stringify(cfg));
        if (!Array.isArray(newCfg.rules)) newCfg.rules = [];
        if (!Array.isArray(newCfg.variables)) newCfg.variables = [];
        newCfg.rules = newCfg.rules.map(r => ({ 
            left: r.left, 
            right: Array.isArray(r.right) ? r.right : ((r.right === 'ε' || r.right === 'epsilon' || r.right === '') ? [] : (typeof r.right === 'string' ? r.right.split('') : [])) 
        }));
        if (newCfg.rules.some(r => r.right.includes(newCfg.startVariable))) {
            const newStart = newCfg.startVariable + '0';
            if (!newCfg.variables.includes(newStart)) newCfg.variables.push(newStart);
            newCfg.rules.push({ left: newStart, right: [newCfg.startVariable] });
            newCfg.startVariable = newStart;
        }
        return newCfg;
    };

    const eliminateNull = (cfg) => {
        const newCfg = JSON.parse(JSON.stringify(cfg));
        if (!Array.isArray(newCfg.rules)) newCfg.rules = [];
        const nullable = new Set();
        newCfg.rules.forEach(r => {
            const rhs = Array.isArray(r.right) ? r.right : ((r.right === 'ε' || r.right === 'epsilon' || r.right === '') ? [] : (typeof r.right === 'string' ? r.right.split('') : []));
            if (rhs.length === 0) nullable.add(r.left);
            r.right = rhs;
        });
        let changed = true;
        let safetyCount = 0;
        while (changed && safetyCount < 100) {
            safetyCount++;
            changed = false;
            for (let r of newCfg.rules) {
                if (r.right.every(sym => nullable.has(sym))) {
                    if (!nullable.has(r.left)) { nullable.add(r.left); changed = true; }
                }
            }
        }
        const newRules = [];
        newCfg.rules.forEach(r => {
            if (r.right.length === 0) return;
            const nullableIndices = [];
            r.right.forEach((sym, i) => { if (nullable.has(sym)) nullableIndices.push(i); });
            
            // Limit to prevent exponential blowup
            const maxCombos = Math.min(1 << nullableIndices.length, 1024);
            for (let i = 0; i < maxCombos; i++) {
                const newRhs = [...r.right];
                for (let j = 0; j < nullableIndices.length; j++) { if ((i >> j) & 1) newRhs[nullableIndices[j]] = null; }
                const finalRhs = newRhs.filter(sym => sym !== null);
                if (finalRhs.length > 0) newRules.push({ left: r.left, right: finalRhs });
            }
        });
        newCfg.rules = newRules;
        return newCfg;
    };

    const eliminateUnit = (cfg) => {
        const newCfg = JSON.parse(JSON.stringify(cfg));
        if (!Array.isArray(newCfg.rules)) newCfg.rules = [];
        if (!Array.isArray(newCfg.variables)) newCfg.variables = [];
        const unitPairs = new Set();
        newCfg.rules.forEach(r => {
            if (!Array.isArray(r.right)) r.right = ((r.right === 'ε' || r.right === 'epsilon' || r.right === '') ? [] : (typeof r.right === 'string' ? r.right.split('') : []));
            if (r.right.length === 1 && newCfg.variables.includes(r.right[0])) unitPairs.add(`${r.left}-${r.right[0]}`);
        });
        const closures = {};
        newCfg.variables.forEach(v => closures[v] = new Set([v]));
        unitPairs.forEach(pair => { const [a, b] = pair.split('-'); if (closures[a]) closures[a].add(b); });
        let changed = true;
        let safetyCount = 0;
        while (changed && safetyCount < 100) {
            safetyCount++;
            changed = false;
            for (let a of newCfg.variables) {
                for (let b of newCfg.variables) {
                    if (closures[a].has(b)) {
                        for (let c of (closures[b] || [])) { if (!closures[a].has(c)) { closures[a].add(c); changed = true; } }
                    }
                }
            }
        }
        const newRules = [];
        for (let A of newCfg.variables) {
            const closureA = closures[A] || new Set([A]);
            for (let B of closureA) {
                newCfg.rules.forEach(p => {
                    if (p.left === B && !(p.right.length === 1 && newCfg.variables.includes(p.right[0]))) newRules.push({ left: A, right: p.right });
                });
            }
        }
        newCfg.rules = newRules;
        return newCfg;
    };

    const separateTerminals = (cfg) => {
        const newCfg = JSON.parse(JSON.stringify(cfg));
        const terminalMap = {};
        newCfg.terminals.forEach(t => {
            const nt = 'X' + t.toUpperCase();
            if (!newCfg.variables.includes(nt)) newCfg.variables.push(nt);
            terminalMap[t] = nt;
            newCfg.rules.push({ left: nt, right: [t] });
        });
        newCfg.rules = newCfg.rules.map(r => {
            if (r.right.length > 1) return { left: r.left, right: r.right.map(sym => terminalMap[sym] || sym) };
            return r;
        });
        return newCfg;
    };

    const binarize = (cfg) => {
        const newCfg = JSON.parse(JSON.stringify(cfg));
        const newRules = [];
        const productionsToProcess = [...newCfg.rules];
        const binarizationVars = ['P', 'Q', 'R', 'T', 'U', 'V', 'W', 'Y', 'Z'];
        let varCounter = 0;
        while (productionsToProcess.length > 0) {
            const p = productionsToProcess.shift();
            if (p.right.length > 2) {
                let newVar = varCounter < binarizationVars.length ? binarizationVars[varCounter] : 'B' + varCounter;
                varCounter++;
                if (!newCfg.variables.includes(newVar)) newCfg.variables.push(newVar);
                newRules.push({ left: p.left, right: [p.right[0], newVar] });
                productionsToProcess.push({ left: newVar, right: p.right.slice(1) });
            } else newRules.push(p);
        }
        const uniqueRules = [];
        const seen = new Set();
        newRules.forEach(r => {
            const key = `${r.left}-${r.right.join(',')}`;
            if (!seen.has(key)) { seen.add(key); uniqueRules.push(r); }
        });
        newCfg.rules = uniqueRules;
        return newCfg;
    };

    const removeDuplicates = (cfg) => {
        const seen = new Set();
        cfg.rules = cfg.rules.filter(r => {
            const key = `${r.left}->${r.right.join(' ')}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        return cfg;
    };

    const cykParse = (cnf, symbols) => {
        const n = symbols.length;
        if (n === 0) return false;
        const table = Array.from({ length: n }, () => Array.from({ length: n }, () => new Set()));
        for (let i = 0; i < n; i++) {
            cnf.rules.forEach(r => { if (r.right.length === 1 && r.right[0] === symbols[i]) table[i][i].add(r.left); });
        }
        for (let len = 2; len <= n; len++) {
            for (let i = 0; i <= n - len; i++) {
                const j = i + len - 1;
                for (let k = i; k < j; k++) {
                    cnf.rules.forEach(r => {
                        if (r.right.length === 2) {
                            const [b, c] = r.right;
                            if (table[i][k].has(b) && table[k + 1][j].has(c)) table[i][j].add(r.left);
                        }
                    });
                }
            }
        }
        return table[0][n - 1].has(cnf.startVariable);
    };

    const generateLeftmostDerivation = (cfg, target) => {
        // Use BFS instead of recursion to prevent deep call stacks and provide better progress
        const queue = [{
            current: cfg.startVariable,
            steps: [{ step: 0, string: cfg.startVariable, production: null, description: `Start with ${cfg.startVariable}` }],
            depth: 1
        }];
        const visited = new Set([cfg.startVariable]);
        const maxDepth = 1000; // Limit iterations
        let count = 0;

        while (queue.length > 0 && count < maxDepth) {
            count++;
            const { current, steps, depth } = queue.shift();

            if (current === target) return steps;
            if (current.length > target.length * 2 + 2) continue;

            let variableIndex = -1;
            let variable = null;
            for (let i = 0; i < current.length; i++) { 
                if (cfg.variables.includes(current[i])) { 
                    variableIndex = i; 
                    variable = current[i]; 
                    break; 
                } 
            }

            if (variable === null) continue;

            const applicableRules = cfg.rules.filter(r => r.left === variable);
            for (const rule of applicableRules) {
                const replacement = (rule.right === 'ε' || rule.right === 'epsilon' || rule.right === '') ? '' : rule.right;
                const newString = current.substring(0, variableIndex) + replacement + current.substring(variableIndex + 1);
                
                // Heuristic: terminal prefix must match target
                const terminalPrefix = newString.split('').filter((_, idx) => idx < variableIndex || !cfg.variables.includes(newString[idx])).join('').substring(0, variableIndex);
                if (terminalPrefix && !target.startsWith(terminalPrefix)) continue;

                if (!visited.has(newString)) {
                    visited.add(newString);
                    const newSteps = [...steps, { 
                        step: depth, 
                        string: newString, 
                        production: rule, 
                        highlightIndices: Array.from({ length: replacement.length }, (_, i) => variableIndex + i), 
                        description: `Apply ${rule.left} → ${rule.right}` 
                    }];
                    queue.push({ current: newString, steps: newSteps, depth: depth + 1 });
                }
            }
        }
        return null;
    };

    const generateTokenDerivation = (cfg, targetTokens) => {
        const targetStr = targetTokens.join(' ');
        const varsSet = new Set(cfg.variables);
        const queue = [{
            current: [cfg.startVariable],
            steps: [{ step: 0, string: cfg.startVariable, production: null, description: `Start with ${cfg.startVariable}` }],
            depth: 1
        }];
        const visited = new Set([cfg.startVariable]);
        const maxIter = 1000;
        let count = 0;

        while (queue.length > 0 && count < maxIter) {
            count++;
            const { current, steps, depth } = queue.shift();
            const currentStr = current.join(' ');

            if (currentStr === targetStr) return steps;
            if (current.length > targetTokens.length * 2 + 2) continue;

            let varIdx = -1;
            for (let i = 0; i < current.length; i++) {
                if (varsSet.has(current[i])) { varIdx = i; break; }
            }
            if (varIdx === -1) continue;

            const variable = current[varIdx];
            const applicableRules = cfg.rules.filter(r => r.left === variable);

            for (const rule of applicableRules) {
                const rhs = tokenizeRHS(rule.right, true);
                const newForm = [...current.slice(0, varIdx), ...rhs, ...current.slice(varIdx + 1)];
                const newStr = newForm.join(' ');

                const termCount = newForm.filter(s => !varsSet.has(s)).length;
                if (termCount > targetTokens.length) continue;

                if (!visited.has(newStr)) {
                    visited.add(newStr);
                    const ruleDisplay = rhs.length === 0 ? 'ε' : rule.right;
                    const newSteps = [...steps, {
                        step: depth,
                        string: newStr,
                        production: rule,
                        highlightIndices: Array.from({ length: rhs.length }, (_, i) => varIdx + i),
                        description: `Apply ${rule.left} → ${ruleDisplay}`
                    }];
                    queue.push({ current: newForm, steps: newSteps, depth: depth + 1 });
                }
            }
        }
        return null;
    };

    const parseString = () => {
        setDerivationSteps([]);
        setCurrentStep(-1);
        setIsAccepted(null);

        const tokenMode = detectTokenMode(cfg);
        const symbols = tokenizeInput(inputString, tokenMode);

        const cnfGrammar = toCNF(cfg, tokenMode);
        const accepted = cykParse(cnfGrammar, symbols);
        
        if (!accepted) {
            setIsAccepted(false);
            setDerivationSteps([{ step: 0, string: inputString, production: null, description: `REJECTED: No derivation possible.` }]);
            setCurrentStep(0);
            return;
        }

        const steps = tokenMode
            ? generateTokenDerivation(cfg, symbols)
            : generateLeftmostDerivation(cfg, inputString);
        if (steps) {
            setDerivationSteps(steps);
            setIsAccepted(true);
            setCurrentStep(0);
        } else {
            setIsAccepted(true);
            setDerivationSteps([{ step: 0, string: inputString, production: null, description: `ACCEPTED (CYK Result)` }]);
            setCurrentStep(0);
        }
    };

    const loadExample = useCallback((exampleName) => {
        const example = examples[exampleName];
        if (example) {
            setCurrentExampleName(exampleName);
            setCurrentExampleDescription(example?.description || null);
            cfg.loadCFG(example);
            setInputString('');
            resetDerivation();
        }
    }, [examples, cfg.loadCFG, resetDerivation]);

    useEffect(() => {
        if (challenge || !tutorialDemoKey) return;
        loadExample(tutorialDemoKey);
        onTutorialDemoConsumed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot deep-link bootstrap
    }, [challenge, tutorialDemoKey]);

    const handleValidateChallenge = () => {
        if (!challenge || !challenge.challenge || !challenge.challenge.testCases) {
            alert('No challenge data available');
            return;
        }
        const userCFG = { variables: cfg.variables, terminals: cfg.terminals, rules: cfg.rules, startVariable: cfg.startVariable };
        const results = validateCFGChallenge(userCFG, challenge.challenge.testCases);
        setValidationResults(results);
        if (window.opener && challenge.returnTo === 'tutorial') {
            window.opener.postMessage({ type: 'CHALLENGE_RESULT', results: results }, window.location.origin);
        }
    };

    const atEnd = currentStep >= 0 && currentStep === derivationSteps.length - 1;

    return (
        <div className="sim">
            <div>
                {challenge?.challenge && (
                    <div className="sim-challenge">
                        <Target size={16} aria-hidden="true" />
                        <span className="sim-challenge-text">
                            <strong>Challenge:</strong> {challenge.challenge.description}
                        </span>
                        {validationResults && (
                            <span
                                className={`verdict ${
                                    validationResults.passed === validationResults.total
                                        ? 'verdict-accept'
                                        : 'verdict-reject'
                                }`}
                            >
                                {validationResults.passed}/{validationResults.total} passing
                            </span>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleValidateChallenge}
                        >
                            <CheckCircle size={14} aria-hidden="true" />
                            Validate
                        </button>
                    </div>
                )}

                <div className="sim-toolbar">
                    <div className="sim-identity">
                        <h1 className="sim-title">Context-Free Grammar</h1>
                        <p className="sim-subtitle">
                            Start symbol {cfg.startVariable} · {cfg.variables.length} variables ·{' '}
                            {cfg.rules.length} productions
                        </p>
                    </div>

                    <div className="sim-run">
                        <label className="sr-only" htmlFor="cfg-input">
                            String to parse
                        </label>
                        <input
                            id="cfg-input"
                            className="field"
                            value={inputString}
                            placeholder="String to parse, e.g. (())"
                            onChange={(e) => setInputString(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && parseString()}
                        />
                        <button type="button" className="btn btn-primary" onClick={parseString}>
                            Parse
                        </button>
                    </div>

                    {isAccepted !== null && (
                        <div
                            className={`verdict ${
                                isAccepted ? 'verdict-accept' : 'verdict-reject'
                            } sim-verdict`}
                        >
                            {isAccepted ? 'Derivable' : 'Not derivable'}
                            <span className="verdict-note">
                                {isAccepted
                                    ? `Derived in ${derivationSteps.length} steps.`
                                    : `No derivation from ${cfg.startVariable}.`}
                            </span>
                        </div>
                    )}
                </div>

                {!challenge && (
                    <div className="sim-examples">
                        <span className="eyebrow sim-examples-label">Examples</span>
                        <div className="sim-examples-list">
                            {Object.entries(examples).map(([key, example]) => (
                                <button
                                    key={key}
                                    type="button"
                                    className="chip"
                                    aria-pressed={currentExampleName === key}
                                    onClick={() => loadExample(key)}
                                >
                                    {example.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {!challenge && currentExampleDescription && (
                    <p className="sim-example-note">{currentExampleDescription}</p>
                )}
            </div>

            <div className="sim-body">
                {/*
                  * The parse tree is this machine's diagram, so it takes the
                  * stage. It used to be the last accordion in the sidebar.
                  */}
                <div className="sim-stage">
                    <div className="card sim-diagram-card">
                        <div className="card-header">
                            <h2 className="card-title">Parse tree</h2>
                            {derivationSteps.length > 0 && (
                                <span className="hint">
                                    Step {currentStep + 1} of {derivationSteps.length}
                                </span>
                            )}
                        </div>
                        <div className="card-body cfg-tree-body">
                            <ParseTree
                                derivationSteps={derivationSteps}
                                currentStep={currentStep}
                                tokenMode={detectTokenMode(cfg)}
                                variables={cfg.variables}
                            />
                        </div>
                    </div>

                    <div className="card">
                        <Transport
                            isPlaying={isPlaying}
                            canPlay={derivationSteps.length > 0 && !atEnd}
                            canStep={derivationSteps.length > 0 && !atEnd}
                            onRun={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onStep={() => {
                                if (currentStep < derivationSteps.length - 1)
                                    setCurrentStep(currentStep + 1);
                            }}
                            onReset={resetDerivation}
                            speed={playbackSpeed}
                            onSpeedChange={setPlaybackSpeed}
                            readouts={[
                                {
                                    label: 'Sentential form',
                                    value:
                                        currentStep >= 0 && derivationSteps[currentStep]
                                            ? derivationSteps[currentStep].sentential ||
                                              derivationSteps[currentStep].current ||
                                              cfg.startVariable
                                            : cfg.startVariable,
                                },
                                {
                                    label: 'Step',
                                    value: `${Math.max(currentStep + 1, 0)}/${
                                        derivationSteps.length || 0
                                    }`,
                                },
                            ]}
                        />
                    </div>
                </div>

                <aside className="sim-panel">
                    {/* The grammar itself is the reference you read while parsing. */}
                    <section className="card">
                        <div className="card-header">
                            <h2 className="card-title">Productions</h2>
                            <span className="hint">
                                terminals {cfg.terminals.join(' ') || '—'}
                            </span>
                        </div>
                        <div className="card-body">
                            {cfg.rules.length === 0 ? (
                                <p className="empty">No productions yet.</p>
                            ) : (
                                <ul className="cfg-rules">
                                    {cfg.rules.map((rule, index) => (
                                        <li className="cfg-rule" key={`${rule.left}-${index}`}>
                                            <span className="cfg-rule-left">{rule.left}</span>
                                            <span className="cfg-rule-arrow">→</span>
                                            <span className="cfg-rule-right">{rule.right}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </section>

                    <CollapsibleSection title="Variables" defaultOpen={!!challenge}>
                        <VariablesEditor cfg={cfg} onUpdate={resetDerivation} />
                    </CollapsibleSection>

                    <CollapsibleSection title="Terminals" defaultOpen={!!challenge}>
                        <TerminalsEditor cfg={cfg} onUpdate={resetDerivation} />
                    </CollapsibleSection>

                    <CollapsibleSection title="Edit productions" defaultOpen={!!challenge}>
                        <ProductionRulesEditor cfg={cfg} onUpdate={resetDerivation} />
                    </CollapsibleSection>

                    {!challenge && (
                        <CollapsibleSection title="Test cases" defaultOpen={false}>
                            <CFGTestCases
                                onLoadTest={(t) => {
                                    setInputString(t);
                                    resetDerivation();
                                }}
                                currentExample={currentExampleName}
                            />
                        </CollapsibleSection>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default CFGSimulator;
